import { useState, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Award,
  BookOpen,
  Flame,
  Trophy,
  Star,
  Mic,
  Sun,
  MessageCircle,
  Zap,
  Map,
  Calendar,
  Moon,
  Lock,
  CheckCircle2,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { useLanguage } from '../contexts/LanguageContext';
import { interpolate } from '../utils/cabinI18n';
import { useBadges } from '../hooks/cabin/useBadges';
import { useMarkBadgeSeen } from '../hooks/cabin/useMarkBadgeSeen';
import CabinErrorState from '../components/cabin/CabinErrorState';

// Map badge icon string → Lucide component
const ICON_MAP = {
  BookOpen,
  Flame,
  Trophy,
  Star,
  Mic,
  Sun,
  MessageCircle,
  Zap,
  Award,
  Map,
  Calendar,
  Moon,
};

const RARITY_CONFIG = {
  common: {
    bg: 'from-emerald-400 to-green-500',
    glow: 'shadow-[0_0_16px_4px_rgba(88,204,2,0.35)]',
    border: 'border-emerald-300',
    pill: 'bg-emerald-100 text-emerald-700',
    label_tr: 'Yaygın',
    label_en: 'Common',
  },
  uncommon: {
    bg: 'from-sky-400 to-blue-500',
    glow: 'shadow-[0_0_20px_5px_rgba(28,176,246,0.4)]',
    border: 'border-sky-300',
    pill: 'bg-sky-100 text-sky-700',
    label_tr: 'Nadir Değil',
    label_en: 'Uncommon',
  },
  rare: {
    bg: 'from-purple-400 to-violet-600',
    glow: 'shadow-[0_0_24px_6px_rgba(206,130,255,0.45)]',
    border: 'border-violet-300',
    pill: 'bg-violet-100 text-violet-700',
    label_tr: 'Nadir',
    label_en: 'Rare',
  },
  epic: {
    bg: 'from-rose-400 to-red-600',
    glow: 'shadow-[0_0_28px_8px_rgba(255,75,75,0.5)]',
    border: 'border-rose-300',
    pill: 'bg-rose-100 text-rose-700',
    label_tr: 'Epik',
    label_en: 'Epic',
  },
  legendary: {
    bg: 'from-amber-400 to-yellow-500',
    glow: 'shadow-[0_0_40px_12px_rgba(255,200,0,0.65)]',
    border: 'border-amber-300',
    pill: 'bg-amber-100 text-amber-700',
    label_tr: 'Efsanevi',
    label_en: 'Legendary',
  },
};

const SORT_OPTIONS = [
  { key: 'recent',   label_tr: 'Son Kazanılanlar', label_en: 'Recently Unlocked' },
  { key: 'rarity',   label_tr: 'Nadirlik',         label_en: 'Rarity'            },
  { key: 'locked',   label_tr: 'Kilitliler Önce',  label_en: 'Locked First'      },
];

const RARITY_ORDER = ['legendary', 'epic', 'rare', 'uncommon', 'common'];

function BadgeIcon({ iconName, size = 28, className = '' }) {
  const IconComp = ICON_MAP[iconName] || Award;
  return <IconComp size={size} strokeWidth={2} className={className} aria-hidden="true" />;
}

function SkeletonCard() {
  return (
    <div className="animate-pulse rounded-2xl border-2 border-gray-200 bg-white p-4 flex flex-col items-center gap-3">
      <div className="w-16 h-16 rounded-2xl bg-gray-200" />
      <div className="h-3.5 w-20 bg-gray-200 rounded" />
      <div className="h-2.5 w-12 bg-gray-200 rounded" />
    </div>
  );
}

function ProgressBar({ value, max, className = '' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div
      className={`h-1.5 rounded-full bg-gray-200 overflow-hidden ${className}`}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemax={max}
      aria-valuemin={0}
    >
      <div
        className="h-full rounded-full bg-gradient-to-r from-sky-400 to-blue-500 transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function BadgeCard({ badge, lang, t: _t, onClick, shouldReduce }) {
  const rarity = badge.rarity || 'common';
  const cfg = RARITY_CONFIG[rarity] || RARITY_CONFIG.common;
  const name = lang === 'tr' ? badge.name_tr : (badge.name_en || badge.name_tr);
  const rarityLabel = lang === 'tr' ? cfg.label_tr : cfg.label_en;
  const isUnlocked = badge.unlocked;

  return (
    <motion.button
      layout
      initial={shouldReduce ? {} : { opacity: 0, scale: 0.93 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={shouldReduce ? {} : { scale: 1.03, y: -2 }}
      whileTap={shouldReduce ? {} : { scale: 0.97 }}
      transition={{ duration: 0.15 }}
      onClick={() => onClick(badge)}
      aria-label={name}
      data-testid={`badge-card-${badge.slug}`}
      className={`relative flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all duration-150 text-left w-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] ${
        isUnlocked
          ? `bg-white ${cfg.border} ${cfg.glow}`
          : 'bg-gray-50 border-gray-200 opacity-70'
      }`}
    >
      {/* Lock overlay icon */}
      {!isUnlocked && (
        <div className="absolute top-2 right-2">
          <Lock size={13} className="text-gray-400" aria-hidden="true" />
        </div>
      )}
      {/* Checkmark for unlocked + unseen */}
      {isUnlocked && !badge.seen && (
        <div className="absolute top-2 right-2">
          <CheckCircle2 size={14} className="text-emerald-500" aria-hidden="true" />
        </div>
      )}

      {/* Badge icon circle */}
      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
          isUnlocked
            ? `bg-gradient-to-br ${cfg.bg} text-white`
            : 'bg-gray-200 text-gray-400'
        }`}
      >
        <BadgeIcon iconName={badge.icon} size={26} />
      </div>

      {/* Name */}
      <p className={`text-xs font-black text-center leading-tight ${
        isUnlocked ? 'text-[var(--text-main)]' : 'text-gray-400'
      }`}>
        {name}
      </p>

      {/* Rarity pill */}
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
        isUnlocked ? cfg.pill : 'bg-gray-100 text-gray-400'
      }`}>
        {rarityLabel}
      </span>

      {/* Progress bar for locked */}
      {!isUnlocked && badge.threshold > 0 && (
        <div className="w-full">
          <ProgressBar value={badge.current_progress} max={badge.threshold} />
          <p className="text-[10px] text-gray-400 text-center mt-0.5">
            {badge.current_progress}/{badge.threshold}
          </p>
        </div>
      )}
    </motion.button>
  );
}

function BadgeDetailSheet({ badge, lang, t, open, onClose }) {
  const { mutate: markSeen } = useMarkBadgeSeen();
  const shouldReduce = useReducedMotion();

  const handleOpen = (isOpen) => {
    if (!isOpen) {
      onClose();
    } else if (badge && badge.unlocked && !badge.seen) {
      markSeen(badge.slug);
    }
  };

  if (!badge) return null;

  const rarity = badge.rarity || 'common';
  const cfg = RARITY_CONFIG[rarity] || RARITY_CONFIG.common;
  const name = lang === 'tr' ? badge.name_tr : (badge.name_en || badge.name_tr);
  const description = lang === 'tr' ? badge.description_tr : (badge.description_en || badge.description_tr);
  const rarityLabel = lang === 'tr' ? cfg.label_tr : cfg.label_en;
  const isUnlocked = badge.unlocked;

  const unlockedDate = badge.unlocked_at
    ? new Date(badge.unlocked_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const remaining = badge.threshold - badge.current_progress;

  return (
    <Sheet open={open} onOpenChange={handleOpen}>
      <SheetContent side="bottom" className="rounded-t-3xl pb-8 max-h-[85vh] overflow-y-auto" data-testid="badge-detail-sheet">
        <SheetHeader className="mb-4">
          <SheetTitle className="sr-only">{name}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col items-center gap-4 pt-2">
          {/* Badge icon */}
          <motion.div
            initial={shouldReduce ? {} : { scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', damping: 12, stiffness: 180 }}
            className={`w-24 h-24 rounded-3xl flex items-center justify-center ${
              isUnlocked
                ? `bg-gradient-to-br ${cfg.bg} text-white ${cfg.glow}`
                : 'bg-gray-200 text-gray-400'
            }`}
          >
            <BadgeIcon iconName={badge.icon} size={40} />
          </motion.div>

          {/* Rarity + name */}
          <div className="text-center">
            <span className={`inline-block text-xs font-black uppercase tracking-wide px-3 py-1 rounded-full mb-2 ${
              isUnlocked ? cfg.pill : 'bg-gray-100 text-gray-400'
            }`}>
              {rarityLabel}
            </span>
            <h2 className="text-xl font-black text-[var(--text-main)]">{name}</h2>
            <p className="text-sm text-[var(--text-muted)] mt-1 max-w-xs mx-auto">{description}</p>
          </div>

          {/* Unlock date or progress */}
          {isUnlocked && unlockedDate ? (
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl px-4 py-3 text-center w-full max-w-xs">
              <CheckCircle2 size={18} className="text-emerald-500 mx-auto mb-1" aria-hidden="true" />
              <p className="text-xs font-bold text-emerald-700">
                {interpolate(t('cabin.gamification.achievements.unlockedOn'), { date: unlockedDate })}
              </p>
            </div>
          ) : (
            <div className="w-full max-w-xs">
              <div className="flex justify-between text-xs text-[var(--text-muted)] mb-1">
                <span>{interpolate(t('cabin.gamification.achievements.progress'), {
                  current: badge.current_progress,
                  threshold: badge.threshold,
                })}</span>
                <span>{Math.round((badge.current_progress / badge.threshold) * 100)}%</span>
              </div>
              <ProgressBar value={badge.current_progress} max={badge.threshold} />
              {remaining > 0 && (
                <p className="text-xs text-[var(--text-muted)] text-center mt-2">
                  {lang === 'tr'
                    ? `${remaining} ders daha tamamla`
                    : `Complete ${remaining} more lessons`}
                </p>
              )}
            </div>
          )}

          {/* Rewards */}
          {(badge.reward_xp > 0 || badge.reward_miles > 0) && (
            <div className="flex items-center gap-3">
              {badge.reward_xp > 0 && (
                <span className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-sm font-black border-2 ${
                  isUnlocked
                    ? 'bg-amber-50 border-amber-200 text-amber-600'
                    : 'bg-gray-50 border-gray-200 text-gray-400'
                }`}>
                  <Zap size={14} aria-hidden="true" />
                  {isUnlocked ? '+' : ''}{badge.reward_xp} XP
                </span>
              )}
              {badge.reward_miles > 0 && (
                <span className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-sm font-black border-2 ${
                  isUnlocked
                    ? 'bg-sky-50 border-sky-200 text-sky-600'
                    : 'bg-gray-50 border-gray-200 text-gray-400'
                }`}>
                  <Star size={14} aria-hidden="true" />
                  {isUnlocked ? '+' : ''}{badge.reward_miles} Mil
                </span>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const FILTER_OPTIONS = ['all', 'unlocked', 'locked'];

export default function CabinAchievements() {
  const { t, lang } = useLanguage();
  const shouldReduce = useReducedMotion();
  const { data, isLoading, isError, refetch } = useBadges();

  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Keep allBadges reference stable so filteredBadges useMemo only re-runs
  // when the actual server data changes, not on every render.
  const allBadges = useMemo(
    () => (Array.isArray(data) ? data : (data?.badges || [])),
    [data]
  );

  const filteredBadges = useMemo(() => {
    let list = [...allBadges];

    // Filter
    if (filter === 'unlocked') list = list.filter((b) => b.unlocked);
    else if (filter === 'locked') list = list.filter((b) => !b.unlocked);

    // Sort
    if (sortBy === 'recent') {
      list.sort((a, b) => {
        if (!a.unlocked_at && !b.unlocked_at) return 0;
        if (!a.unlocked_at) return 1;
        if (!b.unlocked_at) return -1;
        return new Date(b.unlocked_at) - new Date(a.unlocked_at);
      });
    } else if (sortBy === 'rarity') {
      list.sort(
        (a, b) =>
          RARITY_ORDER.indexOf(a.rarity) - RARITY_ORDER.indexOf(b.rarity)
      );
    } else if (sortBy === 'locked') {
      list.sort((a, b) => {
        if (!a.unlocked && b.unlocked) return -1;
        if (a.unlocked && !b.unlocked) return 1;
        return 0;
      });
    }

    return list;
  }, [allBadges, filter, sortBy]);

  const unlockedCount = allBadges.filter((b) => b.unlocked).length;
  const totalCount = allBadges.length;

  const handleBadgeClick = (badge) => {
    setSelectedBadge(badge);
    setSheetOpen(true);
  };

  const handleSheetClose = () => {
    setSheetOpen(false);
    setTimeout(() => setSelectedBadge(null), 300);
  };

  if (isError && !isLoading) {
    return (
      <CabinErrorState
        onRetry={refetch}
        backPath="/programs/kabin-memurlugu"
        testId="achievements-error"
      />
    );
  }

  const filterLabel = (key) => {
    if (key === 'all') return t('cabin.gamification.achievements.filterAll');
    if (key === 'unlocked') return t('cabin.gamification.achievements.filterUnlocked');
    if (key === 'locked') return t('cabin.gamification.achievements.filterLocked');
    return key;
  };

  const allUnlocked = totalCount > 0 && unlockedCount === totalCount;
  const showEmpty = filter === 'locked' && allUnlocked;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="cabin-achievements-page">
      {/* Page header */}
      <div className="flex items-center gap-2 mb-2">
        <Award size={22} className="text-amber-500" aria-hidden="true" strokeWidth={2.5} />
        <h1 className="text-xl font-black text-[var(--text-main)]">
          {t('cabin.gamification.achievements.title')}
        </h1>
      </div>

      {/* Progress summary */}
      {!isLoading && totalCount > 0 && (
        <motion.div
          initial={shouldReduce ? {} : { opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5 p-4 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-2xl border-2 border-amber-400 shadow-[0_4px_0_0_#D97706] text-white"
          data-testid="achievements-summary"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-black">
              {unlockedCount} / {totalCount}
            </p>
            <p className="text-xs font-bold opacity-80">
              {Math.round((unlockedCount / totalCount) * 100)}%
            </p>
          </div>
          <div className="h-2 rounded-full bg-white/30 overflow-hidden">
            <div
              className="h-full rounded-full bg-white transition-all duration-700"
              style={{ width: `${Math.round((unlockedCount / totalCount) * 100)}%` }}
            />
          </div>
        </motion.div>
      )}

      {/* Filters + sort row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        {/* Filter chips */}
        <div
          className="flex gap-1.5"
          role="group"
          aria-label={lang === 'tr' ? 'Rozet filtresi' : 'Badge filter'}
        >
          {FILTER_OPTIONS.map((key) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              data-testid={`filter-chip-${key}`}
              aria-pressed={filter === key}
              className={`px-3 py-1.5 rounded-xl text-xs font-black border-2 transition-all duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] ${
                filter === key
                  ? 'bg-[var(--brand-primary)] border-[var(--brand-primary)] text-white shadow-[0_2px_0_0_var(--brand-primary-shadow)]'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {filterLabel(key)}
            </button>
          ))}
        </div>

        {/* Sort select */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          aria-label={lang === 'tr' ? 'Sıralama' : 'Sort by'}
          data-testid="sort-select"
          className="ml-auto text-xs font-bold border-2 border-gray-200 rounded-xl px-3 py-2 bg-white text-[var(--text-main)] focus:outline-none focus:border-[var(--brand-primary)] transition-colors"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {lang === 'tr' ? opt.label_tr : opt.label_en}
            </option>
          ))}
        </select>
      </div>

      {/* Badge grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : showEmpty ? (
        <motion.div
          initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="py-16 flex flex-col items-center gap-2 text-center"
          data-testid="all-unlocked-empty"
        >
          <Trophy size={40} className="text-amber-400" aria-hidden="true" />
          <p className="text-base font-black text-[var(--text-main)]">
            {t('cabin.gamification.achievements.allUnlocked')}
          </p>
        </motion.div>
      ) : filteredBadges.length === 0 && allBadges.length === 0 ? (
        <motion.div
          initial={shouldReduce ? {} : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="py-16 flex flex-col items-center gap-3 text-center"
          data-testid="achievements-empty"
        >
          <span className="text-5xl" aria-hidden="true">🏅</span>
          <p className="text-base font-black text-[var(--text-main)]">
            {t('cabin.gamification.achievements.empty.title') || 'Henüz rozet yok'}
          </p>
          <p className="text-sm text-[var(--text-muted)] max-w-xs">
            {t('cabin.gamification.achievements.empty.sub') || 'İlk dersi tamamla, ilk rozeti aç!'}
          </p>
        </motion.div>
      ) : filteredBadges.length === 0 ? (
        <motion.div
          initial={shouldReduce ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          className="py-12 text-center text-[var(--text-muted)] text-sm"
        >
          {lang === 'tr' ? 'Rozet bulunamadı' : 'No badges found'}
        </motion.div>
      ) : (
        <AnimatePresence mode="popLayout">
          <motion.div
            layout
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
            aria-live="polite"
            aria-atomic="false"
          >
            {filteredBadges.map((badge) => (
              <BadgeCard
                key={badge.slug}
                badge={badge}
                lang={lang}
                t={t}
                onClick={handleBadgeClick}
                shouldReduce={shouldReduce}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      )}

      {/* Badge detail sheet */}
      <BadgeDetailSheet
        badge={selectedBadge}
        lang={lang}
        t={t}
        open={sheetOpen}
        onClose={handleSheetClose}
      />
    </div>
  );
}
